import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  CreditCard,
  Gift,
  Leaf,
  MapPin,
  MessageCircle,
  Package,
  Pause,
  Play,
  ShoppingBag,
  Sparkles,
  Star,
  Store,
  Volume2,
  VolumeX,
} from "lucide-react";
import ZiniHubIcon from "../../assets/zinihub_icon_RoundEdge.svg";
import { LANDING_LABELS } from "../../labels/landingLabels";
import { LANDING_CONFIG } from "../../config/landingConfig";
import styles from "./Landing.module.css";

const { BRAND, HEADER, HERO, STATUS_CARDS } = LANDING_LABELS;
const { VIDEO, LINKS } = LANDING_CONFIG;

function Header() {
  const navigate = useNavigate();
  return (
    <header className={styles.header}>
      <button
        type="button"
        className={styles.logoButton}
        onClick={() => navigate("/")}
        aria-label={`${BRAND.NAME} home`}
      >
        <img src={ZiniHubIcon} alt={BRAND.NAME} className={styles.logo} />
      </button>
      <nav className={styles.headerNav} aria-label="Primary">
        <button type="button" className={styles.headerSecondaryBtn} onClick={() => navigate(LINKS.SIGN_IN)}>
          {HEADER.SIGN_IN}
        </button>
        <button type="button" className={styles.headerPrimaryBtn} onClick={() => navigate(LINKS.CREATE_ACCOUNT)}>
          {HEADER.CREATE_ACCOUNT}
        </button>
      </nav>
    </header>
  );
}

function Background() {
  return (
    <div className={styles.background} aria-hidden="true">
      <span className={styles.meshGlow} />
      <span className={styles.blobOne} />
      <span className={styles.blobTwo} />
      <span className={styles.blobThree} />
      <span className={styles.dotPattern} />
      <span className={styles.videoGlow} />
    </div>
  );
}

const DOODLE_STROKE = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

function DoodleScribble() {
  return (
    <svg viewBox="0 0 80 32" width="100%" height="100%" aria-hidden="true">
      <path
        d="M4 26 C 12 8, 22 4, 30 16 S 48 28, 56 14 S 72 4, 76 18"
        stroke="currentColor" strokeWidth="2.2" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleWave() {
  return (
    <svg viewBox="0 0 60 24" width="100%" height="100%" aria-hidden="true">
      <path
        d="M4 16 C 10 6, 16 6, 22 16 S 34 26, 40 16 S 52 6, 56 12"
        stroke="currentColor" strokeWidth="2.2" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleLoop() {
  return (
    <svg viewBox="0 0 24 28" width="100%" height="100%" aria-hidden="true">
      <path
        d="M12 24 C 12 14, 12 10, 12 8 C 12 4, 8 4, 7 8 C 6 13, 14 16, 16 10"
        stroke="currentColor" strokeWidth="1.8" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleStar() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <path d="M12 2 C 13 8, 16 11, 22 12 C 16 13, 13 16, 12 22 C 11 16, 8 13, 2 12 C 8 11, 11 8, 12 2 Z" fill="currentColor" />
    </svg>
  );
}

function DoodleHeart() {
  return (
    <svg viewBox="0 0 24 22" width="100%" height="100%" aria-hidden="true">
      <path
        d="M12 20 C 4 14, 2 6, 6 3 C 9 1, 12 4, 12 6 C 12 4, 15 1, 18 3 C 22 6, 20 14, 12 20 Z"
        stroke="currentColor" strokeWidth="1.6" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleArrow() {
  return (
    <svg viewBox="0 0 48 24" width="100%" height="100%" aria-hidden="true">
      <path
        d="M4 14 C 16 14, 26 8, 40 6 M34 4 L42 6 L36 14"
        stroke="currentColor" strokeWidth="1.6" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleBag() {
  return (
    <svg viewBox="0 0 24 26" width="100%" height="100%" aria-hidden="true">
      <path
        d="M7 9 L7 7 C 7 4, 9 3, 12 3 C 15 3, 17 4, 17 7 L17 9 M5 9 L19 9 L18 23 C 18 24, 16 24, 14 24 L10 24 C 8 24, 6 24, 6 23 Z"
        stroke="currentColor" strokeWidth="1.6" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodlePlant() {
  return (
    <svg viewBox="0 0 28 30" width="100%" height="100%" aria-hidden="true">
      <path
        d="M14 28 L14 14 M14 18 C 8 16, 5 18, 5 14 C 8 12, 12 14, 14 18 M14 15 C 16 10, 21 10, 22 13 C 20 16, 16 15, 14 15 M14 21 C 11 22, 8 25, 9 27 C 12 27, 14 24, 14 21"
        stroke="currentColor" strokeWidth="1.6" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleCircle() {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">
      <path
        d="M20 5 C 32 6, 36 18, 32 27 C 27 36, 10 35, 7 24 C 4 13, 12 6, 22 8"
        stroke="currentColor" strokeWidth="1.8" {...DOODLE_STROKE}
      />
    </svg>
  );
}

function DoodleBlob() {
  return (
    <svg viewBox="0 0 44 36" width="100%" height="100%" aria-hidden="true">
      <path
        d="M22 4 C 30 4, 40 8, 40 18 C 40 28, 32 32, 22 32 C 12 32, 4 28, 4 16 C 4 8, 12 4, 22 4 Z"
        fill="currentColor" opacity="0.85"
      />
    </svg>
  );
}

function DoodleDots() {
  return (
    <svg viewBox="0 0 40 20" width="100%" height="100%" aria-hidden="true">
      <circle cx="6" cy="14" r="2.2" fill="currentColor" />
      <circle cx="16" cy="7" r="1.6" fill="currentColor" />
      <circle cx="27" cy="13" r="2.4" fill="currentColor" />
      <circle cx="35" cy="6" r="1.4" fill="currentColor" />
    </svg>
  );
}

function DoodleConfetti() {
  return (
    <svg viewBox="0 0 36 28" width="100%" height="100%" aria-hidden="true">
      <path
        d="M4 6 L12 10 M20 4 L26 3 M10 20 L8 26 M24 16 L32 20 M30 6 L34 9"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      />
    </svg>
  );
}

const BACK_DOODLES = [
  { key: "blobHeadline", Doodle: DoodleBlob, anim: styles.animBob, dur: "9s", delay: "0.8s", style: { top: "33%", left: "4%", width: 96, height: 78, opacity: 0.22, color: "#f59e0b", "--rot": "8deg" } },
  { key: "waveVideo", Doodle: DoodleWave, anim: styles.animFloat, dur: "12s", delay: "1.4s", style: { top: "10%", right: "4%", width: 120, height: 48, opacity: 0.2, color: "#7c3aed", "--rot": "6deg" } },
  { key: "circleVideoBottom", Doodle: DoodleCircle, anim: styles.animSway, dur: "10s", delay: "0.5s", style: { bottom: "9%", right: "16%", width: 84, height: 84, opacity: 0.2, color: "#1B6B82", "--rot": "12deg" } },
  { key: "scribbleBottomLeft", Doodle: DoodleScribble, anim: styles.animBob, dur: "11s", delay: "1.1s", style: { bottom: "10%", left: "5%", width: 100, height: 40, opacity: 0.18, color: "#22c55e", "--rot": "-6deg" } },
  { key: "loopTopCenter", Doodle: DoodleLoop, anim: styles.animFloat, dur: "9s", delay: "2s", style: { top: "7%", right: "32%", width: 44, height: 50, opacity: 0.22, color: "#06b6d4", "--rot": "14deg" } },
  { key: "dotsBehindCta", Doodle: DoodleDots, anim: styles.animBob, dur: "10s", delay: "0.4s", style: { top: "66%", left: "2%", width: 64, height: 32, opacity: 0.2, color: "#f97316", "--rot": "-8deg" } },
  { key: "blobCorner", Doodle: DoodleBlob, anim: styles.animFloat, dur: "13s", delay: "1.7s", style: { top: "1%", right: "1%", width: 72, height: 60, opacity: 0.16, color: "#2BA09E", "--rot": "-14deg" } },
];

const FRONT_DOODLES = [
  { key: "starAccent", Doodle: DoodleStar, anim: styles.animBreathe, dur: "7s", delay: "0.3s", style: { top: "26%", left: "42%", width: 26, height: 26, opacity: 0.32, color: "#f59e0b", "--rot": "0deg" } },
  { key: "heartSub", Doodle: DoodleHeart, anim: styles.animBob, dur: "8s", delay: "1.2s", style: { top: "53%", left: "7%", width: 22, height: 20, opacity: 0.28, color: "#f97316", "--rot": "-10deg" } },
  { key: "bagCta", Doodle: DoodleBag, anim: styles.animFloat, dur: "9s", delay: "0.9s", style: { top: "71%", left: "5%", width: 22, height: 24, opacity: 0.3, color: "#14b8a6", "--rot": "-6deg" } },
  { key: "arrowCta", Doodle: DoodleArrow, anim: styles.animBob, dur: "8s", delay: "1.6s", style: { top: "63%", left: "24%", width: 56, height: 28, opacity: 0.28, color: "#7c3aed", "--rot": "4deg" } },
  { key: "plantLeft", Doodle: DoodlePlant, anim: styles.animSway, dur: "10s", delay: "0.6s", style: { bottom: "13%", left: "15%", width: 26, height: 28, opacity: 0.28, color: "#22c55e", "--rot": "10deg" } },
  { key: "starVideoTop", Doodle: DoodleStar, anim: styles.animBreathe, dur: "8s", delay: "2.1s", style: { top: "24%", right: "47%", width: 20, height: 20, opacity: 0.3, color: "#1B6B82", "--rot": "0deg" } },
  { key: "confettiVideo", Doodle: DoodleConfetti, anim: styles.animFloat, dur: "9s", delay: "1.1s", style: { top: "18%", right: "9%", width: 48, height: 36, opacity: 0.28, color: "#06b6d4", "--rot": "-8deg" } },
  { key: "scribbleUnderVideo", Doodle: DoodleScribble, anim: styles.animSway, dur: "11s", delay: "0.3s", style: { bottom: "4%", right: "22%", width: 72, height: 28, opacity: 0.26, color: "#f97316", "--rot": "-12deg" } },
  { key: "loopHeadline", Doodle: DoodleLoop, anim: styles.animBob, dur: "8s", delay: "1.8s", style: { top: "20%", left: "12%", width: 26, height: 30, opacity: 0.28, color: "#f59e0b", "--rot": "6deg" } },
  { key: "dotsVideoBottom", Doodle: DoodleDots, anim: styles.animBreathe, dur: "9s", delay: "0.7s", style: { bottom: "7%", left: "36%", width: 44, height: 22, opacity: 0.26, color: "#14b8a6", "--rot": "8deg" } },
  { key: "heartHeader", Doodle: DoodleHeart, anim: styles.animFloat, dur: "10s", delay: "2.4s", style: { top: "5%", right: "19%", width: 18, height: 16, opacity: 0.26, color: "#ef4444", "--rot": "12deg" } },
  { key: "arrowSignup", Doodle: DoodleArrow, anim: styles.animSway, dur: "9s", delay: "1.3s", style: { top: "73%", left: "31%", width: 40, height: 20, opacity: 0.26, color: "#1B6B82", "--rot": "-6deg" } },
  { key: "waveVideoMid", Doodle: DoodleWave, anim: styles.animBob, dur: "10s", delay: "0.1s", style: { top: "46%", right: "2%", width: 64, height: 26, opacity: 0.24, color: "#22c55e", "--rot": "10deg" } },
  { key: "blobBottomCenter", Doodle: DoodleBlob, anim: styles.animFloat, dur: "12s", delay: "1.9s", style: { bottom: "2%", left: "42%", width: 40, height: 34, opacity: 0.22, color: "#7c3aed", "--rot": "-10deg" } },
  { key: "starEyebrow", Doodle: DoodleStar, anim: styles.animBob, dur: "8s", delay: "2.7s", style: { top: "30%", left: "1.5%", width: 18, height: 18, opacity: 0.28, color: "#06b6d4", "--rot": "0deg" } },
  { key: "circleVideoGap", Doodle: DoodleCircle, anim: styles.animBreathe, dur: "9s", delay: "1.5s", style: { top: "36%", right: "7%", width: 44, height: 44, opacity: 0.24, color: "#2BA09E", "--rot": "18deg" } },
];

function DoodleBackLayer() {
  return (
    <div className={styles.doodleLayer} aria-hidden="true">
      {BACK_DOODLES.map(({ key, Doodle, anim, dur, delay, style }) => (
        <span
          key={key}
          className={`${styles.doodle} ${anim}`}
          style={{ ...style, animationDuration: dur, animationDelay: delay, "--doodle-opacity": style.opacity }}
        >
          <Doodle />
        </span>
      ))}
    </div>
  );
}

function DoodleFrontLayer() {
  return (
    <div className={styles.doodleLayer} aria-hidden="true">
      {FRONT_DOODLES.map(({ key, Doodle, anim, dur, delay, style }) => (
        <span
          key={key}
          className={`${styles.doodle} ${anim}`}
          style={{ ...style, animationDuration: dur, animationDelay: delay, "--doodle-opacity": style.opacity }}
        >
          <Doodle />
        </span>
      ))}
    </div>
  );
}

function DecorLayer() {
  return (
    <div className={styles.decorLayer} aria-hidden="true">
      <Sparkles className={styles.decorSparkle} size={18} strokeWidth={1.5} />
      <Star className={styles.decorStar} size={12} strokeWidth={1.5} />
      <Star className={styles.decorStarSmall} size={10} strokeWidth={1.5} />
      <ShoppingBag className={styles.decorBag} size={16} strokeWidth={1.5} />
      <Leaf className={styles.decorLeaf} size={18} strokeWidth={1.5} />
      <span className={styles.decorCurveOne} />
      <span className={styles.decorCurveTwo} />
      <span className={styles.decorDot} />
    </div>
  );
}

const STATUS_CARD_ITEMS = [
  { key: "storeLive", Icon: Store, label: STATUS_CARDS.STORE_LIVE, className: styles.cardStoreLive },
  { key: "newOrder", Icon: Package, label: STATUS_CARDS.NEW_ORDER, className: styles.cardNewOrder },
  { key: "payment", Icon: CreditCard, label: STATUS_CARDS.PAYMENT_RECEIVED, className: styles.cardPayment },
  { key: "review", Icon: MessageCircle, label: STATUS_CARDS.NEW_REVIEW, className: styles.cardReview },
  { key: "nearby", Icon: MapPin, label: STATUS_CARDS.NEARBY_CUSTOMER, className: styles.cardNearby },
];

function StatusCards() {
  return (
    <div className={styles.statusCards} aria-hidden="true">
      {STATUS_CARD_ITEMS.map(({ key, Icon, label, className }) => (
        <div key={key} className={`${styles.statusCard} ${className}`}>
          <span className={styles.statusCardIcon}>
            <Icon size={13} strokeWidth={2} />
          </span>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function VideoCard() {
  const playerRef = useRef(null);
  const indexRef = useRef(0);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const videoIds = VIDEO.YOUTUBE_IDS;
    if (!videoIds || videoIds.length === 0) return undefined;

    const nextVideo = () => {
      indexRef.current = (indexRef.current + 1) % videoIds.length;
      playerRef.current?.loadVideoById(videoIds[indexRef.current]);
    };

    let player;
    let cleanupPoll = null;
    let readyPoll = null;

    const disableCaptions = (ytPlayer) => {
      if (!ytPlayer) return;
      try {
        ytPlayer.unloadModule("captions");
        ytPlayer.unloadModule("cc");
        ytPlayer.setOption("captions", "track", {});
      } catch {
        /* YouTube may ignore caption APIs on some embeds */
      }
    };

    const onReady = () => {
      player.mute();
      disableCaptions(player);
      player.playVideo();
      setPlaying(true);
    };

    const onStateChange = (event) => {
      if (event.data === window.YT.PlayerState.PLAYING) {
        disableCaptions(player);
        setPlaying(true);
      }
      if (event.data === window.YT.PlayerState.PAUSED) {
        setPlaying(false);
      }
      if (event.data === window.YT.PlayerState.ENDED) {
        nextVideo();
      }
    };

    const initPlayer = () => {
      if (!window.YT || !window.YT.Player) return;
      player = new window.YT.Player("zinihub-landing-video", {
        videoId: videoIds[0],
        playerVars: {
          autoplay: 1,
          mute: 1,
          playsinline: 1,
          controls: 0,
          cc_load_policy: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0,
        },
        events: {
          onReady,
          onStateChange,
        },
      });
      playerRef.current = player;
    };

    const loadApi = () => {
      if (window.YT && window.YT.Player) {
        initPlayer();
        return;
      }
      window.onYouTubeIframeAPIReady = initPlayer;
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.async = true;
      const firstScript = document.getElementsByTagName("script")[0];
      firstScript?.parentNode?.insertBefore(tag, firstScript);
    };

    loadApi();

    return () => {
      window.onYouTubeIframeAPIReady = undefined;
      window.clearTimeout(cleanupPoll);
      window.clearTimeout(readyPoll);
      player?.destroy();
      playerRef.current = null;
    };
  }, []);

  const toggleMute = () => {
    const player = playerRef.current;
    if (!player) return;
    if (muted) {
      player.unMute();
      player.setVolume(100);
      setMuted(false);
    } else {
      player.mute();
      setMuted(true);
    }
  };

  const togglePlay = () => {
    const player = playerRef.current;
    if (!player) return;
    if (playing) {
      player.pauseVideo();
      setPlaying(false);
    } else {
      player.playVideo();
      setPlaying(true);
    }
  };

  return (
    <div className={styles.videoWrap}>
      <div className={styles.phoneShell}>
        <span className={styles.phoneBtnSilent} aria-hidden="true" />
        <span className={styles.phoneBtnVolumeUp} aria-hidden="true" />
        <span className={styles.phoneBtnVolumeDown} aria-hidden="true" />
        <span className={styles.phoneBtnPower} aria-hidden="true" />
        <div className={styles.phoneScreen}>
          <div className={styles.phoneIsland} aria-hidden="true">
            <span className={styles.phoneIslandLens} />
            <span className={styles.phoneIslandCamera} />
          </div>
          <div id="zinihub-landing-video" className={styles.videoPlayer} title={HERO.VIDEO_TITLE} />
          <div className={styles.videoControls}>
            <button
              type="button"
              className={styles.videoControlBtn}
              onClick={togglePlay}
              aria-label={playing ? HERO.VIDEO_PAUSE : HERO.VIDEO_PLAY}
              aria-pressed={!playing}
            >
              {playing ? <Pause size={16} strokeWidth={2.2} /> : <Play size={16} strokeWidth={2.2} />}
            </button>
            <button
              type="button"
              className={styles.videoControlBtn}
              onClick={toggleMute}
              aria-label={muted ? HERO.VIDEO_UNMUTE : HERO.VIDEO_MUTE}
              aria-pressed={!muted}
            >
              {muted ? <VolumeX size={16} strokeWidth={2.2} /> : <Volume2 size={16} strokeWidth={2.2} />}
            </button>
          </div>
          <span className={styles.phoneHomeIndicator} aria-hidden="true" />
        </div>
      </div>
      <StatusCards />
    </div>
  );
}

function HeroContent() {
  const navigate = useNavigate();
  const displayLine1Accent = "Smart Store Generator";
  const displayLine1Rest = HERO.SUBHEADING_LINE_1.slice(displayLine1Accent.length);
  const displayLine2Accent = "Be live";
  const displayLine2Rest = HERO.SUBHEADING_LINE_2.slice(displayLine2Accent.length);

  return (
    <div className={styles.heroContent}>
      <p className={styles.eyebrow}>{HERO.HEADLINE}</p>
      <h1 className={styles.displayTitle}>
        <span className={styles.displayLine}>
          <span className={styles.textAccent}>{displayLine1Accent}</span>
          {displayLine1Rest}
        </span>
        <span className={`${styles.displayLine} ${styles.displayLineSub}`}>
          <span className={styles.textAccent}>{displayLine2Accent}</span>
          {displayLine2Rest}
        </span>
      </h1>
      <div
        className={styles.offerBadge}
        aria-label={`${HERO.OFFER_PERCENT} ${HERO.OFFER_COMMISSION}`}
      >
        <div className={styles.offerTop}>
          <span className={styles.offerPercent} aria-hidden="true">{HERO.OFFER_PERCENT}</span>
          <span className={styles.offerCommission} aria-hidden="true">{HERO.OFFER_COMMISSION}</span>
        </div>
        <ul className={styles.offerBenefits}>
          {HERO.OFFER_BENEFITS.map((benefit) => (
            <li key={benefit} className={styles.offerBenefit}>
              <span className={styles.offerBenefitIcon} aria-hidden="true">
                <Gift size={13} strokeWidth={2.2} />
              </span>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.ctaRow}>
        <button type="button" className={styles.primaryCta} onClick={() => navigate(LINKS.FREE_SIGN_UP)}>
          {HERO.PRIMARY_CTA}
          <ArrowRight size={17} aria-hidden="true" />
        </button>
        <a
          className={styles.secondaryCta}
          href={LINKS.CONTACT_US}
          target="_blank"
          rel="noopener noreferrer"
        >
          {HERO.SECONDARY_CTA}
        </a>
      </div>
      <p className={styles.footnote}>{HERO.FOOTNOTE}</p>
    </div>
  );
}

export default function Landing() {
  useEffect(() => {
    const root = document.documentElement;
    document.body.setAttribute("data-zh-landing", "1");
    root.style.background = "#FFF9F2";
    root.style.overflowX = "clip";
    document.body.style.overflowX = "clip";
    return () => {
      document.body.removeAttribute("data-zh-landing");
      root.style.background = "";
      root.style.overflowX = "";
      document.body.style.overflowX = "";
    };
  }, []);

  return (
    <main className={styles.page}>
      <Background />
      <DoodleBackLayer />
      <div className={styles.content}>
        <Header />
        <section className={styles.hero}>
          <HeroContent />
          <div className={styles.heroMedia}>
            <VideoCard />
          </div>
        </section>
      </div>
      <DecorLayer />
      <DoodleFrontLayer />
    </main>
  );
}
